import React, { useEffect, useMemo, useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { useMountedSafeState } from 'features/sendouts/hooks/useMountedSafeState';
import strings from 'strings';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { UIStatus } from 'UI/constants/status';

import { SendoutInterviewDetailsTabs } from './SendoutInterviewDetailsTabs';
import { INTERVIEW_DRAWER_MODES, TAB_IDS } from './SendoutInterviewDrawer.constants';
import { INTERVIEW_TAB_CONFIG } from './SendoutInterviewDrawer.utils';
import { useInterviewTabData } from './useInterviewTabData';

export const SendoutInterviewDetailsDrawer = ({
  interviewId,
  mode = INTERVIEW_DRAWER_MODES.View,
  onClose = () => {},
  onSaved = () => {}
}) => {
  const isEditMode = mode === INTERVIEW_DRAWER_MODES.Edit;
  const [selectedTab, setSelectedTab] = useState(TAB_IDS.InterviewDetails);
  const [isLoading, setIsLoading] = useMountedSafeState(false);
  const [isFormPreloaded, setIsFormPreloaded] = useState(false);

  const form = useForm();
  const { clearError, handleSubmit, reset, setError } = form;

  const tab = INTERVIEW_TAB_CONFIG[selectedTab];
  const { state } = useInterviewTabData({ endpoint: tab.endpoint, interviewId });

  const isFetching = state.status === UIStatus.Loading;
  const hasLoadError = state.status === UIStatus.Error;
  const record = state.results;
  const isRecordMissing = state.status === UIStatus.Success && !record;
  const values = useMemo(() => (record ? tab.buildDefaultValues(record) : null), [record, tab]);

  useEffect(() => {
    reset(values ?? {});
    setIsFormPreloaded(!!values);
  }, [reset, values]);

  const canUpdate =
    isEditMode && !isFetching && !hasLoadError && (isFormPreloaded || isRecordMissing);

  const handleSave = async formData => {
    if (tab.validate && !tab.validate({ clearError, formData, setError })) return;

    setIsLoading(true);
    try {
      await onSaved(tab.buildSubmitArgs(formData, { existingItemId: record?.id ?? null }));
    } finally {
      setIsLoading(false);
    }
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.update,
        variant: 'contained',
        disabled: !canUpdate || isLoading,
        onClick: handleSubmit(handleSave)
      },
      GridItemProps: { md: 3 }
    }
  ];

  const handleChangeTabIndex = (_event, tabId) => setSelectedTab(tabId);

  return (
    <FPDrawer
      FPDrawerProps={{ open: true }}
      CardHeaderProps={{ title: strings.sendoutsV2.drawers.sendoutInterviewDetails.title }}
      footer={
        isEditMode ? (
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        ) : undefined
      }
      onClose={onClose}
      hasPaddingContent={false}
    >
      <FormContext {...form}>
        <SendoutInterviewDetailsTabs
          hasLoadError={hasLoadError}
          isEditMode={isEditMode}
          isFetching={isFetching}
          isFormPreloaded={isFormPreloaded}
          isRecordMissing={isRecordMissing}
          onChangeTabIndex={handleChangeTabIndex}
          record={record}
          selectedTab={selectedTab}
        />
      </FormContext>
    </FPDrawer>
  );
};
